import { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts"
import dashboardService from "../../services/DashboardService.js"
import { Users, Package, Banknote, AlertTriangle, Wallet, Truck, LayoutDashboard, ChevronDown } from "lucide-react"
import { PageLayout, Button, StatusBadge } from "../common/index.js"

const formatCurrency = (amount) => {
  const num = Math.abs(Number(amount) || 0)
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`
  return `₹${num.toFixed(0)}`
}

const AdminDashboard = () => {
  const quickLinksRef = useRef(null);
  const navigate = useNavigate()
  const [statsLoading, setStatsLoading] = useState(true)
  const [chartLoading, setChartLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(true)

  const [globalDateRange, setGlobalDateRange] = useState("this-month")
  const [customStartDate, setCustomStartDate] = useState("")
  const [customEndDate, setCustomEndDate] = useState("")
  const [openMenuId, setOpenMenuId] = useState(null)

  const [userStats, setUserStats] = useState({ total: 0, filtered: 0 })
  const [shipmentStats, setShipmentStats] = useState({ total: 0, filtered: 0 })
  const [revenueStats, setRevenueStats] = useState({ total: 0, filtered: 0, paid: 0 })
  const [dueStats, setDueStats] = useState({ total: 0, filtered: 0 })
  const [commissionStats, setCommissionStats] = useState({ total: 0, pending: 0, paid: 0, records: 0 })
  const [vehicleStats, setVehicleStats] = useState({ total: 0, filtered: 0 })

  const [recentShipments, setRecentShipments] = useState([])
  const [recentPayments, setRecentPayments] = useState([])
  const [recentCommissions, setRecentCommissions] = useState([])
  const [revenueData, setRevenueData] = useState([])

  const getDateRange = useCallback(() => {
    const now = new Date()
    let from, to

    if (globalDateRange === "this-month") {
      from = new Date(now.getFullYear(), now.getMonth(), 1)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    } else if (globalDateRange === "last-3-months") {
      from = new Date(now.getFullYear(), now.getMonth() - 3, 1)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    } else if (globalDateRange === "last-6-months") {
      from = new Date(now.getFullYear(), now.getMonth() - 6, 1)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    } else if (globalDateRange === "last-year") {
      from = new Date(now.getFullYear() - 1, now.getMonth(), 1)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    } else if (globalDateRange === "all-time") {
      from = null
      to = null
    } else if (globalDateRange === "custom") {
      from = customStartDate ? new Date(customStartDate) : null
      to = customEndDate ? new Date(customEndDate) : null
    }

    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return {
      from: from ? formatDate(from) : null,
      to: to ? formatDate(to) : null,
    }
  }, [globalDateRange, customStartDate, customEndDate])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        quickLinksRef.current &&
        !quickLinksRef.current.contains(event.target)
      ) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Single overview fetch for all KPI cards
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setStatsLoading(true)
        const r = getDateRange()
        const data = await dashboardService.getOverview({ fromDate: r.from, toDate: r.to })

        setUserStats({ total: data.users.total_active, filtered: data.users.total_active })
        setShipmentStats({ total: data.shipments.total_count, filtered: data.shipments.total_count })
        setRevenueStats({ total: data.revenue_due.total_revenue, filtered: data.revenue_due.total_revenue, paid: data.shipments.paid_amount })
        setDueStats({ total: data.revenue_due.total_due, filtered: data.revenue_due.total_due })
        setVehicleStats({ total: data.vehicles.total, filtered: data.vehicles.total })
        setCommissionStats({
          total: data.commission.grand_total.total,
          paid: data.commission.grand_total.paid,
          pending: data.commission.grand_total.pending,
          records: data.commission.total_records,
        })
      } catch (error) {
        console.error("Error fetching dashboard overview:", error)
      } finally {
        setStatsLoading(false)
      }
    }
    fetchOverview()
  }, [getDateRange])

  // Recent shipments
  useEffect(() => {
    const fetchRecentShipments = async () => {
      try {
        const response = await dashboardService.getShipmentRecords({ page: 1, perPage: 5 })
        setRecentShipments(response.shipments || [])
      } catch (error) {
        console.error("Error fetching recent shipments:", error)
      } finally {
        setTableLoading(false)
      }
    }
    fetchRecentShipments()
  }, [])

  // Recent payments
  useEffect(() => {
    const fetchRecentPayments = async () => {
      try {
        const response = await dashboardService.getPaymentRecords({ page: 1, perPage: 5 })
        setRecentPayments(response.payments || [])
      } catch (error) {
        console.error("Error fetching recent payments:", error)
      }
    }
    fetchRecentPayments()
  }, [])

  // Recent commissions
  useEffect(() => {
    const fetchRecentCommissions = async () => {
      try {
        const response = await dashboardService.getCommissionRecords({ page: 1, perPage: 5 })
        setRecentCommissions(response.commissions || [])
      } catch (error) {
        console.error("Error fetching recent commissions:", error)
      }
    }
    fetchRecentCommissions()
  }, [])

  // Revenue chart data
  useEffect(() => {
    const fetchRevenueData = async () => {
      try {
        const dateRange = getDateRange()
        const response = await dashboardService.getShipmentRecords({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          perPage: 1000,
        })
        const allShipments = response.shipments || []

        const intervalData = {}
        allShipments.forEach((shipment) => {
          const date = new Date(shipment.created_at)
          const year = date.getFullYear()
          const month = date.getMonth() + 1
          const day = date.getDate()

          let interval
          if (day <= 10) interval = 1
          else if (day <= 20) interval = 2
          else interval = 3

          const key = `${year}-${String(month).padStart(2, "0")}-I${interval}`
          const label = `${String(month).padStart(2, "0")}/${year} (${interval === 1 ? "1-10" : interval === 2 ? "11-20" : "21-31"})`

          if (!intervalData[key]) {
            intervalData[key] = { period: label, paid: 0, due: 0, sortKey: key }
          }

          intervalData[key].paid += parseFloat(shipment.paid_amount) || 0
          intervalData[key].due += parseFloat(shipment.balance_amount) || 0
        })

        const chartData = Object.values(intervalData)
          .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
          .slice(-12)

        setRevenueData(chartData)
      } catch (error) {
        console.error("Error fetching revenue data:", error)
      } finally {
        setChartLoading(false)
      }
    }
    fetchRevenueData()
  }, [getDateRange])

  return (
    <PageLayout
      title="Welcome, Admin"
      icon={LayoutDashboard}
      maxWidth="max-w-8xl"
      actions={
        <>
          <div ref={quickLinksRef} className="relative">
            <Button
              size="sm"
              icon={ChevronDown}
              iconPosition="right"
              onClick={() => setOpenMenuId(openMenuId === "quickLinks" ? null : "quickLinks")}
            >
              Quick Links
            </Button>
            {openMenuId === "quickLinks" && (
              <div className="absolute left-0 mt-2 w-44 bg-white border border-gray-200 rounded-card shadow-card z-20 py-1">
                <button onClick={() => { navigate("/shipments/add"); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-secondary">Book Shipment</button>
                <button onClick={() => { navigate("/ml-sheets"); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-secondary">ML Sheets</button>
                <button onClick={() => { navigate("/daily-earnings"); setOpenMenuId(null); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-secondary">Daily Earnings</button>
              </div>
            )}
          </div>
          <select
            value={globalDateRange}
            onChange={(e) => {
              setGlobalDateRange(e.target.value)
              if (e.target.value !== "custom") {
                setCustomStartDate("")
                setCustomEndDate("")
              }
            }}
            className="input-clean w-auto text-sm py-1.5"
          >
            <option value="this-month">This Month</option>
            <option value="last-3-months">Last 3 Months</option>
            <option value="last-6-months">Last 6 Months</option>
            <option value="last-year">Last Year</option>
            <option value="all-time">All Time</option>
            <option value="custom">Custom</option>
          </select>
        </>
      }
    >
      {globalDateRange === "custom" && (
        <div className="mb-6 p-4 bg-white rounded-card shadow-card flex gap-4 items-end flex-wrap">
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">Start Date</label>
            <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="input-clean" />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">End Date</label>
            <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="input-clean" />
          </div>
        </div>
      )}

              {/* Stats Grid - 6 Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                {/* Card 1: Total Active Users */}
                <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-medium transition-shadow relative min-h-[110px] lg:min-h-0">
                  <div className="flex mb-3">
                    <div className="flex-1">
                      <p className="text-secondary text-sm font-medium">Active Users</p>
                      {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{userStats.filtered}</p>}
                    </div>
                  </div>
                  <div className="p-2 absolute rounded-md right-2 bottom-2 bg-brand">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Card 2: Total Shipments */}
                <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-medium transition-shadow relative min-h-[110px] lg:min-h-0">
                  <div className="flex mb-3">
                    <div className="flex-1">
                      <p className="text-secondary text-sm font-medium">Shipments</p>
                      {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{shipmentStats.filtered}</p>}
                    </div>
                  </div>
                  <div className="p-2 absolute rounded-md right-2 bottom-2 bg-green-600">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                </div>

                {/* Card 3: Total Revenue */}
                <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-medium transition-shadow relative min-h-[110px] lg:min-h-0">
                  <div className="flex mb-3">
                    <div className="flex-1">
                      <p className="text-secondary text-sm font-medium">Total Amount</p>
                      {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{formatCurrency(revenueStats.filtered)}</p>}
                      <p className="text-sm text-secondary mt-3 absolute font-regular">Paid: {formatCurrency(revenueStats.paid)}</p>
                    </div>
                    <div className="bg-purple-500 p-2 absolute rounded-md right-2 bottom-2">
                      <Banknote className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>

                {/* Card 4: Total Due Amount */}
                <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-medium transition-shadow relative min-h-[110px] lg:min-h-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-secondary text-sm font-medium">Outstanding</p>
                      {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{formatCurrency(dueStats.filtered)}</p>}
                    </div>
                    <div className="bg-blue-600 p-2 absolute rounded-md right-2 bottom-2">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>

                {/* Card 5: Commission */}
                <div className="bg-white rounded-card shadow-card p-4 pt-3 hover:shadow-medium transition-shadow relative min-h-[110px]">
                  <div className="flex mb-3">
                    <div className="flex-1">
                      <p className="text-secondary text-sm font-medium">Commission</p>
                      {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{formatCurrency(commissionStats.pending)}</p>}
                      <p className="text-sm text-secondary mt-3 absolute font-regular">Total: {formatCurrency(commissionStats.total)}</p>
                    </div>
                    <div className="bg-red-500 p-2 absolute rounded-md right-2 bottom-2">
                      <Wallet className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>

                {/* Card 6: Total Vehicles */}
                <div className="bg-white rounded-card shadow-card p-4 hover:shadow-medium transition-shadow relative">
                  <div className="flex mb-3">
                    <div className="flex-1">
                      <p className="text-secondary text-sm font-medium">Vehicles</p>
                      {statsLoading ? <div className="h-6 w-16 bg-gray-200 animate-pulse rounded mt-1" /> : <p className="text-lg font-bold text-dark mt-1">{vehicleStats.filtered}</p>}
                    </div>
                    <div className="bg-indigo-500 p-2 absolute rounded-md right-2 bottom-2">
                      <Truck className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Shipments, Payments, Commissions */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Recent Shipments */}
                <div className="bg-white rounded-card shadow-card p-4 md:p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-md md:text-md font-semibold text-dark">Recent Shipments</h2>
                    <Button variant="ghost" size="sm" onClick={() => navigate("/shipments")}>View All →</Button>
                  </div>
                  <div className="space-y-3">
                      {tableLoading ? (
                        <div className="flex justify-center py-6">
                          <div className="w-6 h-6 border-4 border-brand border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : recentShipments.length > 0 ? (
                        recentShipments.map((shipment) => (
                        <div
                          key={shipment.shipment_id || shipment.id}
                          className="p-3 bg-light rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-regular text-dark text-sm">{shipment.shipment_id}</p>
                              <p className="text-xs text-secondary">{shipment.sender_name}</p>
                            </div>
                            <StatusBadge status={shipment.status} />
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-secondary text-sm text-center py-4">No recent shipments</p>
                    )}
                  </div>
                </div>
                {/* Recent Payments */}
                <div className="bg-white rounded-card shadow-card p-4 md:p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-md md:text-md font-semibold text-tertiary mb-0">Recent Payments</h2>
                    <Button variant="ghost" size="sm" onClick={() => navigate("/payment-history")}>View All →</Button>
                  </div>
                  <div className="space-y-3">
                    {recentPayments.length > 0 ? (
                      recentPayments.map((payment) => (
                        <div
                          key={payment.id}
                          className="p-3 bg-light rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => navigate(`/payment-history/view/${payment.id}`)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-regular text-dark text-sm">
                                ₹{parseFloat(payment.payment_amount || 0).toLocaleString()}
                              </p>
                              <p className="text-xs text-secondary">Shipment: {payment.shipment_id}</p>
                              <p className="text-xs text-secondary">{payment.payment_mode}</p>
                            </div>
                            <p className="text-xs text-secondary">
                              {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-secondary text-sm text-center py-4">No recent payments</p>
                    )}
                  </div>
                </div>

                {/* Recent Commissions (replaced Pending Returns) */}
                <div className="bg-white rounded-card shadow-card p-4 md:p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-md md:text-md font-semibold text-tertiary mb-0">Recent Commissions</h2>
                    <Button variant="ghost" size="sm" onClick={() => navigate("/commissions")}>View All →</Button>
                  </div>
                  <div className="space-y-3">
                    {recentCommissions.length > 0 ? (
                      recentCommissions.map((comm) => (
                        <div
                          key={comm.commission_id || comm.id}
                          className="p-3 bg-light rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => navigate(`/commissions/view/${comm.commission_id}`)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-regular text-dark text-sm">{comm.commission_id}</p>
                              <p className="text-xs text-secondary">ML: {comm.ml_sheet_id}</p>
                            </div>
                            <StatusBadge status={comm.company_payment_status || 'pending'} />
                          </div>
                          <div className="flex justify-between text-xs text-secondary">
                            <span>{comm.company_name || ''}</span>
                            <span>{comm.owner_name || ''}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-secondary text-sm text-center py-4">No recent commissions</p>
                    )}
                  </div>
                </div>
              </div>
              {/* Revenue Graph */}
                <div className="lg:col-span-2 bg-white rounded-card shadow-card p-4 md:p-6">
                  <h2 className="text-lg md:text-md font-regular text-tertiary mb-4">
                    Revenue & Due Analysis (10-Day Intervals)
                  </h2>
                  {chartLoading ? (
                      <div className="flex items-center justify-center h-[300px]">
                        <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                      <Legend />
                      <Line type="monotone" dataKey="paid" stroke="#10b981" strokeWidth={2} name="Paid Amount" />
                      <Line type="monotone" dataKey="due" stroke="#f97316" strokeWidth={2} name="Due Amount" />
                    </LineChart>
                    </ResponsiveContainer>
                    )}
                </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 text-right mt-4">
        <p className="text-md font-medium text-gray-500">Designed with Passion by <span className="text-brand">DigiQin</span></p>
      </div>
    </PageLayout>
  )
}

export default AdminDashboard;
